import { ArrowDownRight, ArrowUpRight, Minus, RefreshCw } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';

export const fmtDateTime = (iso) => iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'Unavailable';

export const Unavailable = ({ text = 'Unavailable' }) => <span className="italic text-slate-400">{text}</span>;

export const Change = ({ pct, testId }) => {
  if (pct === null || pct === undefined) return <span className="text-xs text-slate-400" data-testid={testId}>No comparison</span>;
  const up = pct > 0;
  const Icon = pct === 0 ? Minus : up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-bold ${pct === 0 ? 'text-slate-500' : up ? 'text-emerald-600' : 'text-rose-600'}`} data-testid={testId}>
      <Icon className="h-3.5 w-3.5" />{Math.abs(pct)}% vs prev. period
    </span>
  );
};

export const StatCard = ({ label, value, Icon, sub, testId, accent = 'text-[#14366C] bg-[#14366C]/10' }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-transform hover:-translate-y-0.5" data-testid={testId}>
    <div className="flex items-start gap-3">
      {Icon && <div className={`grid h-10 w-10 shrink-0 place-items-center rounded-full ${accent}`}><Icon className="h-5 w-5" /></div>}
      <div className="min-w-0">
        <p className="font-oswald text-3xl leading-none text-slate-900" data-testid={`${testId}-value`}>{value ?? <Unavailable />}</p>
        <p className="mt-1.5 text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">{label}</p>
        {sub && <div className="mt-2">{sub}</div>}
      </div>
    </div>
  </div>
);

export const Panel = ({ title, Icon, children, testId, right }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={testId}>
    <div className="mb-3 flex items-center justify-between gap-2">
      <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#F58220]">{Icon && <Icon className="h-3.5 w-3.5" />}{title}</p>
      {right}
    </div>
    {children}
  </div>
);

export const BarList = ({ items, testId, empty = 'No data for this period' }) => {
  if (!items?.length) return <p className="py-3 text-sm italic text-slate-400" data-testid={`${testId}-empty`}>{empty}</p>;
  const max = Math.max(...items.map((i) => i.count));
  return (
    <ul className="space-y-1.5" data-testid={testId}>
      {items.map((i) => (
        <li key={i.label} className="relative overflow-hidden rounded-md px-2 py-1.5">
          <div className="absolute inset-y-0 left-0 rounded-md bg-[#14366C]/[0.07]" style={{ width: `${(i.count / max) * 100}%` }} />
          <div className="relative flex justify-between gap-3 text-sm">
            <span className={`truncate ${i.label === 'Unavailable' ? 'italic text-slate-400' : 'text-slate-700'}`}>{i.label}</span>
            <span className="font-mono text-slate-600">{i.count}</span>
          </div>
        </li>
      ))}
    </ul>
  );
};

export const TrendChart = ({ data, testId, color = '#14366C' }) => (
  <div className="h-48" data-testid={testId}>
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
        <CartesianGrid vertical={false} stroke="#EEF1F5" />
        <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} minTickGap={16} />
        <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
        <Tooltip cursor={{ fill: '#F58220', fillOpacity: 0.08 }} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
        <Bar dataKey="count" fill={color} radius={[4, 4, 0, 0]} maxBarSize={28} />
      </BarChart>
    </ResponsiveContainer>
  </div>
);

export const RefreshedAt = ({ at, onRefresh, loading, testId = 'refresh-btn' }) => (
  <div className="flex items-center gap-3">
    <span className="hidden text-xs text-slate-500 sm:inline" data-testid={`${testId}-time`}>Updated {at ? new Date(at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '—'}</span>
    <button onClick={onRefresh} className="inline-flex h-10 items-center gap-2 rounded-full border border-slate-200 bg-white px-4 text-sm font-semibold shadow-sm hover:border-[#14366C]" data-testid={testId}>
      <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />Refresh
    </button>
  </div>
);
